import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  forwardRef,
  OnInit,
  OnDestroy,
  inject,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { OverlayModule } from '@angular/cdk/overlay';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { FilterOperator } from '../../../utils/filter-operator';
import { GenericSelectorConfig } from './generic-selector.models';

@Component({
  selector: 'app-generic-selector',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, OverlayModule],
  templateUrl: './generic-selector.component.html',
  styleUrls: ['./generic-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GenericSelectorComponent),
      multi: true,
    },
  ],
})
export class GenericSelectorComponent<T>
  implements OnInit, OnDestroy, ControlValueAccessor
{
  @Input() config!: GenericSelectorConfig<T>;
  @Input() placeholder: string = 'SELECT_ELLIPSIS';
  @Input() label: string = '';
  @Input() disabled: boolean = false;

  @Output() selectionChange = new EventEmitter<T | null>();

  @ViewChild('trigger', { read: ElementRef }) trigger!: ElementRef;
  @ViewChild('optionsList') optionsList!: ElementRef;

  isOpen = false;
  triggerWidth = 0;

  items: T[] = [];
  selectedItem: T | null = null;

  totalCount = 0;
  searchTerm = '';
  isLoading = false;
  private currentPage = 0;
  @Input() pageSize: number = 20;
  private destroy$ = new Subject<void>();
  private searchSubject = new Subject<string>();
  private readonly cdr = inject(ChangeDetectorRef);

  onChange: any = () => {};
  onTouch: any = () => {};

  ngOnInit() {
    this.searchSubject
      .pipe(debounceTime(400), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((term) => {
        this.searchTerm = term;
        this.loadData(true);
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleDropdown(event?: Event) {
    event?.stopPropagation();
    if (this.disabled) return;

    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      const rect = this.trigger.nativeElement.getBoundingClientRect();
      this.triggerWidth = rect.width;
      if (this.items.length === 0 || this.totalCount === 0) {
        this.loadData(true);
      }
    } else {
      this.onTouch();
    }
    this.cdr.markForCheck();
  }

  onSearch(term: string) {
    this.searchSubject.next(term);
  }

  /**
   * Основной метод загрузки данных
   * @param reset Если true, сбрасывает список и грузит первую страницу (для поиска или открытия)
   */
  loadData(reset: boolean = false) {
    if (!this.config) return;

    if (reset) {
      this.currentPage = 0;
      this.items = [];
      this.isLoading = true;
      this.totalCount = 0;
    } else {
      if (this.items.length >= this.totalCount && this.totalCount > 0) return;
      this.isLoading = true;
    }
    this.cdr.markForCheck();

    const offset = this.currentPage * this.pageSize;
    const filters: any[] = [];

    if (this.searchTerm) {
      filters.push({
        filterField: this.config.searchField,
        filterOperator: FilterOperator.Contains,
        filterValue: this.searchTerm,
      });
    }

    const queryParams = {
      offset: offset,
      take: this.pageSize,
      filters: filters,
    };

    this.config
      .getItems(queryParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (list) => {
          this.isLoading = false;
          if (reset) {
            this.items = list;
            if (list.length < this.pageSize) {
              this.totalCount = list.length;
            } else if (this.totalCount === 0) {
              this.totalCount = 999999;
            }
          } else {
            this.items = [...this.items, ...list];
            if (list.length < this.pageSize) {
              this.totalCount = this.items.length;
            }
          }

          if (list.length > 0) {
            this.currentPage++;
          }
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });

    if (reset) {
      this.config
        .getTotalCount({ filters })
        .pipe(takeUntil(this.destroy$))
        .subscribe((count) => {
          this.totalCount = count;
          this.cdr.markForCheck();
        });
    }
  }

  onScroll(event: any) {
    const element = event.target;
    if (element.scrollHeight - element.scrollTop <= element.clientHeight + 50) {
      if (!this.isLoading && this.items.length < this.totalCount) {
        this.loadData(false);
      }
    }
  }

  selectItem(item: T) {
    this.selectedItem = item;
    this.onChange(item[this.config.idField]);
    this.selectionChange.emit(item);
    this.isOpen = false;
    this.cdr.markForCheck();
  }

  clearSelection(event: Event) {
    event.stopPropagation();
    this.selectedItem = null;
    this.onChange(null);
    this.selectionChange.emit(null);
    this.cdr.markForCheck();
  }

  getDisplayValue(item: T): any {
    if (this.config.formatLabel) {
      return this.config.formatLabel(item);
    }
    return item ? item[this.config.displayField] : '';
  }

  writeValue(value: any): void {
    if (!value) {
      this.selectedItem = null;
      this.cdr.markForCheck();
      return;
    }
    const foundItem = this.items.find((i) => i[this.config.idField] === value);
    if (foundItem) {
      this.selectedItem = foundItem;
      this.cdr.markForCheck();
    } else if (this.config.getItemById) {
      this.isLoading = true;
      this.cdr.markForCheck();

      this.config.getItemById(value).subscribe({
        next: (item) => {
          this.selectedItem = item;
          this.isLoading = false;
          const exists = this.items.some(
            (i) => i[this.config.idField] === item[this.config.idField]
          );
          if (!exists) {
            this.items = [item, ...this.items];
          }
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }
}
